// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentJudgmentActions.test.tsx
import { ArrowClockwise, Heart, Trash } from "@phosphor-icons/react";
import { Flex, IconButton, Tooltip } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

export type AskV2ReviewCommentJudgmentActionsProps = {
  commentId: string;
  liked?: boolean | null;
  disabled?: boolean;
  /** Keep the actions visible without hover: the open card, or a judged one. */
  pinned?: boolean;
  /**
   * Heart writes `liked: true` (press again to withdraw to null). Delete on a
   * finding already at `liked: false` restores it to null.
   */
  onJudge?: (liked: boolean | null) => void;
  /** Delete on a finding that is not already deleted asks for scope first. */
  onRequestDelete?: () => void;
};

/**
 * Header actions on an AI finding: a heart and a delete icon. Both write the
 * existing `liked` field. Leaving a finding alone already means it was useful,
 * so the heart is only a stronger "love" (`liked: true`) and delete is
 * `liked: false`.
 */
export function AskV2ReviewCommentJudgmentActions({
  commentId,
  liked = null,
  disabled = false,
  pinned = false,
  onJudge,
  onRequestDelete,
}: AskV2ReviewCommentJudgmentActionsProps) {
  const { t } = useTranslation("ui");
  // why: not deleting already means useful, so heart is `liked: true` and delete is `liked: false` on that same field
  const isLoved = liked === true;
  const isDeleted = liked === false;
  const likeLabel = isLoved
    ? t("askV2.review.removeLike")
    : t("askV2.review.likeFinding");
  const deleteLabel = isDeleted
    ? t("assetViewer.restore")
    : t("askV2.review.deleteFinding");

  if (onJudge == null) return null;

  return (
    <Flex
      gap="2"
      align="center"
      className={`shrink-0 transition-opacity ${
        pinned
          ? ""
          : "opacity-0 focus-within:opacity-100 group-hover/comment:opacity-100"
      }`}
      data-testid={`ask-v2-review-comment-judgment-${commentId}`}
      data-pinned={pinned ? "true" : "false"}
    >
      <Tooltip content={likeLabel}>
        <IconButton
          type="button"
          size="1"
          variant="soft"
          color={isLoved ? "red" : undefined}
          aria-label={likeLabel}
          aria-pressed={isLoved}
          disabled={disabled}
          onClick={() => onJudge(isLoved ? null : true)}
          // why: the Phosphor heart fills with `currentColor`, and Tailwind's
          // utilities load after the Radix stylesheet at equal specificity, so
          // an unconditional gray utility beats `color="red"` and a loved
          // finding renders a filled GRAY heart. Set the red here, the way the
          // delete icon below already does.
          className={
            isLoved ? "text-puntt-red-9" : "text-puntt-neutral-gray-11"
          }
          data-testid={`ask-v2-review-comment-like-${commentId}`}
        >
          <Heart
            weight={isLoved ? "fill" : "regular"}
            height="16"
            width="16"
            aria-hidden="true"
          />
        </IconButton>
      </Tooltip>
      <Tooltip content={deleteLabel}>
        <IconButton
          type="button"
          size="1"
          variant="soft"
          color={isDeleted ? "red" : undefined}
          aria-label={deleteLabel}
          aria-pressed={isDeleted}
          disabled={disabled}
          onClick={() => (isDeleted ? onJudge(null) : onRequestDelete?.())}
          className={
            isDeleted
              ? "text-puntt-neutral-gray-11"
              : "text-puntt-red-9 transition-colors hover:bg-puntt-red-9 hover:text-base-white"
          }
          data-testid={`ask-v2-review-comment-delete-${commentId}`}
        >
          {isDeleted ? (
            <ArrowClockwise height="16" width="16" aria-hidden="true" />
          ) : (
            <Trash height="16" width="16" aria-hidden="true" />
          )}
        </IconButton>
      </Tooltip>
    </Flex>
  );
}
